import { removeSpaces } from "./case-utils";
import { isValidDate, parseDate } from "./date-utils";


export function removeSpecialCharacters(value: string): string {
       value = value.replace(/[^\x20-\x7E]/g, "");
       return value;
}
/**
 * Checks whether the provided text represents a non-negative integer.
 * @param value Text input to validate.
 * @returns True when the value contains only digits and has no leading zeros;
 * otherwise false. The value `0` is valid.
 */
export function isInteger(value: string): boolean {
    return /^(0|[1-9][0-9]*)$/.test(value);
}

/**
 * Normalizes a heading string by removing special characters, converting to lowercase,
 * and replacing non-alphanumeric characters with underscores.
 * @param value Heading text to normalize.
 * @returns Normalized heading string.
 */
export function normalizeHeading(value: string): string {
    return value
        .normalize("NFKC")
        .toLowerCase()
        .replace(/[\r\n]+/g, " ")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
}
/**
 * Determines if the supplied string can be parsed as a floating-point number.
 * @param value Text input to evaluate.
 * @returns True when the value follows a floating-point format; otherwise false.
 */
export function isFloat(value: string): boolean {
    return /^[-+]?[0-9]*\.[0-9]{1,}$$/.test(value);
}

/**
 * Evaluates whether the string represents a boolean-like token.
 * @param value Input text potentially containing a boolean indicator.
 * @returns True for `true`, `false`, `t`, or `f` (case-insensitive); otherwise false.
 */
export function isBoolean(value: string): boolean {
    let lcValue = value?.trim().toLowerCase();
    return lcValue === 'true' || lcValue === 'false' || lcValue === 'f' || lcValue === 't';
}

/**
 * Generates a padding string comprised of a repeated character.
 * @param length Total number of characters to include.
 * @param padding Character to repeat within the padding string.
 * @returns String of the requested length containing the padding character.
 */
function buildPadding(length: number, padding: "0" | " "): string {
    let array = new Array<string>(length);
    for (let i = 0; i < array.length; i++) {
        array[i] = padding;
    }

    return array.join('');
}

/**
 * Pads a numeric or string value on the left using zeros until reaching the target length.
 * @param length Desired total length of the result.
 * @param value Value to pad on the left side.
 * @returns Zero-padded string representation of the value.
 */
export function zeroPadString(length: number, value: number | string): string {
    var padding = buildPadding(length - value.toString().length, "0");
    return `${padding}${value}`;
}


/**
 * Validates that a name conforms to JavaScript identifier rules.
 * @param name Candidate identifier to inspect.
 * @returns True when the name is a valid identifier; otherwise false.
 */
export const isValidFieldName = (name: string): boolean => {
   
    // Regular expression to check if it's a valid JS identifier
    const identifierRegex = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/;

    return (
        identifierRegex.test(name) 
    );
};


    /**
     * Compares two objects and returns the property names whose values differ.
     * @param objA Baseline object for comparison.
     * @param objB Object containing potential changes.
     * @returns Array of keys where the values differ between the two objects.
     */


export const changedFields = (objA: any, objB: any) => {
    let keys = Object.keys(objA)
    let fields = new Array<string>();

    keys.forEach(k => {
        if (objA[k] !== objB[k])
            fields.push(k)
    })
    return fields;
  
}


/**
 * Calculate the SHA-256 hash of a file (e.g., PDF) in the browser.
 * @param file The File object (from an <input type="file">)
 * @returns Promise resolving to the hexadecimal hash string
 */
export async function hashFile(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  // Convert ArrayBuffer to hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}


/**
 * Convenience wrapper to compute a SHA-256 hash for a PDF file.
 * @param file PDF file to hash.
 * @returns Promise resolving to the hexadecimal hash string.
 */
export async function hashPdf(file: File): Promise<string> {
    return await hashFile(file);
} 


/**
 * Rounds a number to a specified number of decimal places.
 * @param value Numeric value to round.
 * @param decimals Number of decimal places to retain.
 * @returns Rounded number using standard rounding rules.
 */
export const roundToDecimals = (value: number, decimals: number): number => {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
}



export interface ParseValue {
    value: any;
    dataType: string;
}


export function isValidInteger(input: string) {

    // Check if the input is a valid decimal number
    const regex = /^[-+]?\d+$/;
    return (regex.test(input));
}

export function removeCommas(input: string): string {
    return input.replace(/,/g, "");
}

/**
 * Removes carriage-return and line-feed characters from text.
 * @param input Text to normalize.
 * @returns Text without carriage-return characters.
 */
export function removeCarriageReturns(input: string): string {
    return input.replace(/[\r\n]/g, "");
}

export function isValidFloat(input: string) {
    const regex = /^-{0,1}\d*\.{0,1}\d+$/;
    return (regex.test(input));
}

export function parseFloatValue(inValue: any): number | undefined {

    
    if (typeof inValue === "boolean")
        throw new Error(`${inValue} is not a valid float`);
    if (typeof inValue === "number") return inValue;
    if (typeof inValue === "string") {
        inValue = removeSpaces(inValue)
        inValue = removeCommas(inValue)
        if (inValue === '-')
            return 0;
        if (isValidFloat(inValue))
            return parseFloat(inValue);
        throw new Error(`${inValue} is not a valid float`);
    }
}

export function parseInteger(inValue: any): number | undefined {

    if (typeof inValue === "boolean")
        throw new Error(`${inValue} is not a valid integer`);
    if (typeof inValue === "number") return inValue;
    if (typeof inValue === "string") {
        inValue = removeSpaces(inValue)
        if (inValue === '-')
            return 0;
        if (isValidInteger(inValue))
            return parseInt(inValue, 10);
        else
            throw new Error(`${inValue} is not a valid integer`);
    }
}


export function parseItemString(inValue: any): string {
    return inValue.toString();
}

export function isValidBoolean(value: string): boolean {
    try {
        let varType = typeof value
        if (varType === "boolean") 
                return true;
        else if (varType === "number")
            return Number(value) === 0 || Number(value) === 1
        else if (varType === "string")
            return value?.toLowerCase() === 'true' || value?.toLowerCase() === 'false'
        return false;
    } catch (ex) {
        console.log(value, ex)
        throw ex;
    }
}


export function parseBoolean(inValue: any): boolean {
    if (typeof inValue === "boolean") return inValue;
    if (typeof inValue === "number") return inValue === 1;
    if (typeof inValue === "string") {
        let value = inValue.toLowerCase();
        return value === "true" || value === 't' || value === "yes";
    } 
    return false;
}


export function parseValue(value: any): ParseValue {
    let result = {
        value: value,
        dataType: "string"
    }
    if (isValidInteger(value)) {
        result.dataType  = "integer";
        result.value = parseInteger(value)
    }
    else if (isValidFloat(value)) {
        result.dataType = "float";
        result.value = parseFloatValue(value)
    }
    else if (isValidDate(value)) {
        result.dataType = "date";
        result.value = parseDate(value)
    }
    else if (isValidBoolean(value)) {
        result.dataType = "boolean";
        result.value= parseBoolean(value)
    }
    
    return result;
}
